import { useState } from 'react';
import { Loader2, ScanSearch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { getSignedUrl } from '@/config/storage';
import { showToast } from '@/utils/toast';
import { createSourceFromFile, isLocalDemoMode } from '../services/price-intel-service';

interface SourceParseButtonProps {
  fileId: string;
  fileName?: string;
  storageKey?: string;
  className?: string;
  compact?: boolean;
}

function guessMimeType(fileName?: string): string {
  const lowerName = fileName?.toLowerCase() || '';
  if (lowerName.endsWith('.pdf')) return 'application/pdf';
  if (lowerName.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (lowerName.endsWith('.xls')) return 'application/vnd.ms-excel';
  if (lowerName.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (lowerName.endsWith('.pptx')) return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  if (lowerName.endsWith('.csv')) return 'text/csv';
  if (lowerName.endsWith('.md') || lowerName.endsWith('.markdown')) return 'text/markdown';
  if (lowerName.endsWith('.txt')) return 'text/plain';
  if (lowerName.endsWith('.png')) return 'image/png';
  if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) return 'image/jpeg';
  if (lowerName.endsWith('.webp')) return 'image/webp';
  return 'application/octet-stream';
}

export function SourceParseButton({
  fileId,
  fileName,
  storageKey,
  className = '',
  compact = false,
}: SourceParseButtonProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSendToParse = async () => {
    try {
      setIsSubmitting(true);
      let result: Awaited<ReturnType<typeof createSourceFromFile>>;

      if (isLocalDemoMode()) {
        if (!storageKey || !fileName) {
          throw new Error('本地模式需要文件存储路径，或请改用总览页直接上传文件。');
        }

        const signedUrl = await getSignedUrl(storageKey);
        const response = await fetch(signedUrl);
        if (!response.ok) {
          throw new Error(`读取原文件失败: ${response.status}`);
        }
        const blob = await response.blob();
        const file = new File([blob], fileName, {
          type: blob.type || guessMimeType(fileName),
        });
        result = await createSourceFromFile(fileId, {
          file,
          fileName,
          storageKey,
          metadata: {
            intake: 'existing_file_detail',
          },
        });
        showToast('本地解析完成，已生成候选事实');
      } else {
        result = await createSourceFromFile(fileId);
        showToast('已送去解析，任务已进入队列');
      }

      if (result.ingestion_job?.id) {
        navigate('/price_intel/jobs');
      }
    } catch (error) {
      console.error('Failed to send file to price intel pipeline:', error);
      showToast(`送去解析失败: ${(error as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <button
      onClick={handleSendToParse}
      disabled={isSubmitting}
      className={className || (compact
        ? 'inline-flex items-center justify-center px-3 py-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-60'
        : 'w-full flex items-center justify-center px-3 py-2 bg-indigo-50 text-indigo-700 text-sm rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-60')}
    >
      {isSubmitting ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <ScanSearch size={15} className="mr-1.5" />}
      {isSubmitting ? '提交中...' : '送去解析'}
    </button>
  );
}
